import React from 'react'
import { Alert, Box, Grid, Typography } from '@mui/material'
import { type KycArchiveViewProps } from './KycArchiveApplicationPage'
import { KycArchiveResultChip } from './KycArchiveResultChip'
import { formatDateTimeAsApiFormat } from '../../../utility/formatters/DateFormatter'

interface FieldItemProps {
  label: string
  value: string | number | null | undefined
}

const FieldItem: React.FC<FieldItemProps> = ({ label, value }: FieldItemProps) => {
  return (
    <Grid item lg={3} md={4} sm={6}>
      <Typography variant={'caption'} color={'text.secondary'} display={'block'}>{label}</Typography>
      <Typography variant={'body1'}>{value !== null && value !== undefined && value !== '' ? value : '-'}</Typography>
    </Grid>
  )
}

const formatDateTime = (value: string | null): string | null => {
  if (value === null) return null
  return formatDateTimeAsApiFormat(new Date(value))
}

const formatDateOnly = (value: string | null): string | null => {
  if (value === null) return null
  return value.substring(0, 10)
}

const KycArchiveOverviewView: React.FC<KycArchiveViewProps> = ({
  application
}: KycArchiveViewProps) => {
  const latestFields = application.latestFields

  return (
    <>
      {application.isReconstructed && (
        <Alert severity='info' sx={{ mb: 3 }}>
          The application record (type, review history and notes) was not included in the Acuant extract.
          This application has been rebuilt from its evaluations; the result shown is the latest evaluation&apos;s decision.
        </Alert>
      )}
      <Box sx={{ pb: 4 }}>
        <h3>Application</h3>
        <Grid container spacing={4} rowSpacing={3}>
          <FieldItem label={'Application Id'} value={application.applicationId}/>
          {!application.isReconstructed && <FieldItem label={'Type'} value={application.type}/>}
          <Grid item lg={3} md={4} sm={6}>
            <Typography variant={'caption'} color={'text.secondary'} display={'block'}>Result</Typography>
            <KycArchiveResultChip result={application.result}/>
          </Grid>
          <FieldItem label={'Created'} value={formatDateTime(application.created)}/>
          <FieldItem label={'Last Update'} value={formatDateTime(application.lastUpdate)}/>
          <FieldItem label={'Most Recent Evaluation'} value={formatDateTime(application.mostRecentEvaluation)}/>
          <FieldItem label={'Latest Transaction Id'} value={application.latestTransactionId}/>
          <FieldItem label={'Evaluations'} value={application.transactionCount}/>
          <FieldItem label={'History Entries'} value={application.historyCount}/>
          <FieldItem label={'Notes'} value={application.noteCount}/>
          <FieldItem label={'Document Images'} value={application.imageCount}/>
          <FieldItem label={'Attachments'} value={application.attachmentCount}/>
        </Grid>
      </Box>

      <Box sx={{ pb: 4 }}>
        <h3>Latest Submitted Details</h3>
        {latestFields === null && (
          <Typography variant={'body1'}>No transaction details available for this application.</Typography>
        )}
        {latestFields !== null && (
          <Grid container spacing={4} rowSpacing={3}>
            <FieldItem label={'Transaction Id'} value={latestFields.transactionId}/>
            <FieldItem label={'Transaction Time'} value={formatDateTime(latestFields.transactionTime)}/>
            <FieldItem label={'First Name'} value={latestFields.firstName}/>
            <FieldItem label={'Middle Name'} value={latestFields.middleName}/>
            <FieldItem label={'Last Name'} value={latestFields.lastName}/>
            <FieldItem label={'Date of Birth'} value={formatDateOnly(latestFields.dateOfBirth)}/>
            <FieldItem label={'Email'} value={latestFields.email}/>
            <FieldItem label={'Secondary Email'} value={latestFields.emailSecondary}/>
            <FieldItem label={'Phone'} value={latestFields.phone}/>
            <FieldItem label={'Street'} value={latestFields.street}/>
            <FieldItem label={'City'} value={latestFields.city}/>
            <FieldItem label={'State / Region'} value={latestFields.stateRegion}/>
            <FieldItem label={'Zip'} value={latestFields.zip}/>
            <FieldItem label={'Country'} value={latestFields.country}/>
            <FieldItem label={'National Id Scheme'} value={latestFields.nationalIdScheme}/>
            <FieldItem label={'National Id'} value={latestFields.nationalId}/>
            <FieldItem label={'Tax Id'} value={latestFields.taxId}/>
            <FieldItem label={'Document Type'} value={latestFields.docType}/>
            <FieldItem label={'Document Country'} value={latestFields.docCountry}/>
            <FieldItem label={'IP Address'} value={latestFields.ipAddress}/>
          </Grid>
        )}
      </Box>

      {/* ==================================================================================================== */}
    </>
  )
}

export default KycArchiveOverviewView
